import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal, Typography, message, Spin, Grid, Layout, Button, Form } from 'antd';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { startAgentTestingConversation, deleteAgentTestingConversation, sendAgentTestingMessage, getVirtualContacts, createVirtualContact, getVirtualContactConversation, deleteVirtualContact } from '../AgentTestingService';
import TestChatInterface from './TestChatInterface/TestChatInterface';
import './AgentTestModal.css';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';
import SidebarList from './SidebarList';
import SidebarNewContact from './SidebarNewContact';
import { RTL_LANGUAGES } from '../../../Constants/language';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Header, Sider, Content } = Layout;

const AgentTestModal = ({ visible, onClose, agent }) => {
    const { t, i18n } = useTranslation();
    const isRTL = RTL_LANGUAGES.includes(i18n.language?.split('-')[0]);
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint();
    const isMobile = !screens.sm;
    const [loading, setLoading] = useState(false);
    const [testConversation, setTestConversation] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [sidebarMode, setSidebarMode] = useState('list'); // 'list' | 'new' | 'chat'
    const [hoveredContactId, setHoveredContactId] = useState(null);
    const [newContactForm] = Form.useForm();
    const [creatingContact, setCreatingContact] = useState(false);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [conversationByContactId, setConversationByContactId] = useState({});
    const conversationCacheRef = useRef({});
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);

    const upsertConversation = useCallback((contactId, conversation) => {
        if (!contactId) return;
        conversationCacheRef.current[contactId] = conversation;
        setConversationByContactId((prev) => ({ ...prev, [contactId]: conversation }));
    }, []);

    const prefetchConversations = useCallback(async (list) => {
        const ids = (Array.isArray(list) ? list : [])
            .map((c) => c?.id)
            .filter(Boolean);
        if (!ids.length) return;

        await Promise.allSettled(ids.map(async (id) => {
            if (conversationCacheRef.current[id]) return;
            try {
                const conv = await getVirtualContactConversation(id);
                upsertConversation(id, conv);
            } catch (e) {
                // silent prefetch failure
            }
        }));
    }, [upsertConversation]);

    const themeColors = {
        background: isDarkMode ? '#141414' : '#fff',
        cardBackground: isDarkMode ? '#1f1f1f' : '#fafafa',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        border: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)'
    };

    const handleStartTest = useCallback(async (forceNew = false) => {
        if (!agent?.id) return;

        setLoading(true);
        try {
            if (forceNew) {
                await deleteAgentTestingConversation(agent.id);
            }
            const conversation = await startAgentTestingConversation(agent.id);
            setTestConversation(conversation);
            setSelectedContactId(conversation?.contact?.id || null);
            // Do not auto-switch views on mobile; keep list visible until user selects
            // Load/refresh sidebar contacts list
            try {
                setContactsLoading(true);
                const list = await getVirtualContacts(agent.id);
                setContacts(Array.isArray(list) ? list : []);
                prefetchConversations(list);
            } catch (err) {
                // Non-blocking sidebar fetch error
            } finally {
                setContactsLoading(false);
            }

            // Show appropriate message only when a new conversation is created
            if (forceNew) {
                message.success(t('test_conversation_started_new'));
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_start_test_conversation'));
        } finally {
            setLoading(false);
        }
    }, [agent?.id, t, prefetchConversations]);

    const handleSendMessage = useCallback(async (messageData) => {
        if (!testConversation?.contact?.id) return;

        try {
            const result = await sendAgentTestingMessage(
                testConversation.contact.id,
                messageData
            );
            return result;
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_send_message'));
            throw error;
        }
    }, [testConversation?.contact?.id, t]);

    const handleClose = useCallback(() => {
        setTestConversation(null);
        setSelectedContactId(null);
        setContacts([]);
        setSidebarMode('list');
        setHoveredContactId(null);
        setConversationByContactId({});
        conversationCacheRef.current = {};
        newContactForm.resetFields();
        onClose();
    }, [onClose, newContactForm]);

    const handleStartNewTest = useCallback(async () => {
        setTestConversation(null);
        await handleStartTest(true); // Force new conversation
    }, [handleStartTest]);

    // Auto-start test conversation when modal opens
    useEffect(() => {
        // Auto-start only on desktop/tablet (screens.sm true). On mobile, show list first.
        if (visible && agent && !testConversation && !loading && screens.sm) {
            handleStartTest();
        }
    }, [visible, agent, testConversation, loading, handleStartTest, screens.sm]);

    // Fetch sidebar contacts whenever modal becomes visible or agent changes
    useEffect(() => {
        const fetch = async () => {
            if (!visible || !agent?.id) return;
            try {
                setContactsLoading(true);
                const list = await getVirtualContacts(agent.id);
                setContacts(Array.isArray(list) ? list : []);
                prefetchConversations(list);
            } catch (error) {
                message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_check_virtual_contacts'));
            } finally {
                setContactsLoading(false);
            }
        };
        fetch();
    }, [visible, agent?.id, t, prefetchConversations]);

    const renderLoadingState = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 40px',
            textAlign: 'center'
        }}>
            <Spin size="large" style={{ marginBottom: '24px' }} />
            <Title level={4} style={{ color: themeColors.text, marginBottom: '8px' }}>
                {t('preparing_test_environment')}
            </Title>
            <Text type="secondary">
                {t('setting_up_virtual_contact_and_conversation')}
            </Text>
        </div>
    );

    const renderChatInterface = () => (
        <TestChatInterface
            agent={agent}
            conversation={testConversation}
            onSendMessage={handleSendMessage}
            onStartNewTest={handleStartNewTest}
            onClose={handleClose}
            isMobile={isMobile}
            onConversationUpdated={(conv) => {
                if (!conv) return;
                setTestConversation(conv);
                const cid = conv?.contact?.id || selectedContactId;
                if (cid) upsertConversation(cid, conv);
            }}
        />
    );

    // Handle mobile back button with multi-step navigation
    useMobileFullscreenModalBackHandler(
        visible && isMobile,
        handleClose,
        {
            canGoBack: () => sidebarMode === 'new' || sidebarMode === 'chat',
            onBack: () => setSidebarMode('list')
        }
    );
    const modalConfig = {
        title: null,
        open: visible,
        onCancel: handleClose,
        footer: null,
        closable: false,
        width: '100vw',
        style: { top: 0, padding: 0, maxWidth: '100vw' },
        styles: {
            body: {
                background: themeColors.background,
                height: '100vh',
                overflow: 'hidden',
                padding: 0
            },
            content: {
                padding: 0,
                height: '100vh',
                position: 'fixed',
                width: '100vw'
            }
        },
        destroyOnClose: false,
        centered: false,
        mask: true,
        maskClosable: true,
        className: 'agent-test-modal'
    };

    const handleSelectContact = useCallback(async (contact) => {
        if (!contact?.id) return;
        try {
            const conv = await getVirtualContactConversation(contact.id);
            upsertConversation(contact.id, conv);
            setTestConversation(conv);
            setSelectedContactId(contact.id);
            if (isMobile) setSidebarMode('chat');
        } catch (e) {
            message.error(resolveBackendMessage(t, e?.response?.data) || t('failed_to_get_virtual_conversation'));
        }
    }, [isMobile, t, upsertConversation]);

    const handleDeleteContact = useCallback((contact) => {
        if (!contact?.id) return;
        setContactToDelete(contact);
        setDeleteModalVisible(true);
    }, []);

    const confirmDeleteContact = useCallback(async () => {
        if (!contactToDelete?.id) return;
        try {
            const payload = await deleteVirtualContact(contactToDelete.id);
            setContacts(prev => prev.filter(c => c.id !== contactToDelete.id));
            setConversationByContactId(prev => {
                const next = { ...prev };
                delete next[contactToDelete.id];
                return next;
            });
            delete conversationCacheRef.current[contactToDelete.id];
            if (selectedContactId === contactToDelete.id) {
                setSelectedContactId(null);
                setTestConversation(null);
            }
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_delete_contact'));
        } finally {
            setDeleteModalVisible(false);
            setContactToDelete(null);
        }
    }, [contactToDelete, selectedContactId, t]);

    const handleCreateContact = useCallback(async () => {
        if (!agent?.id) return;
        try {
            setCreatingContact(true);
            const values = await newContactForm.validateFields();
            const created = await createVirtualContact(agent.id, values);
            const list = await getVirtualContacts(agent.id);
            setContacts(Array.isArray(list) ? list : []);
            if (created?.id) {
                const conv = await getVirtualContactConversation(created.id);
                setTestConversation(conv);
                setSelectedContactId(created.id);
            }
            newContactForm.resetFields();
            setSidebarMode('list');
            message.success(t('test_conversation_started'));
        } catch (error) {
            if (error?.errorFields) return; // validation errors
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_create_virtual_contact'));
        } finally {
            setCreatingContact(false);
        }
    }, [agent?.id, newContactForm, t]);

    const renderSidebarList = () => (
        <SidebarList
            contacts={contacts}
            contactsLoading={contactsLoading}
            selectedContactId={selectedContactId}
            testConversation={testConversation}
            conversationByContactId={conversationByContactId}
            hoveredContactId={hoveredContactId}
            setHoveredContactId={setHoveredContactId}
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            t={t}
            onAddContact={() => setSidebarMode('new')}
            onSelectContact={handleSelectContact}
            onDeleteContact={handleDeleteContact}
        />
    );

    const renderSidebarNewContact = () => (
        <SidebarNewContact
            form={newContactForm}
            creatingContact={creatingContact}
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            themeColors={themeColors}
            t={t}
            onBack={() => setSidebarMode('list')}
            onCreate={handleCreateContact}
        />
    );

    return (
        <Modal {...modalConfig}>
            <Layout style={{ height: '100%', background: themeColors.background }}>
                <Header style={{
                    background: themeColors.background,
                    borderBottom: `1px solid ${themeColors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {isMobile && sidebarMode === 'chat' && (
                            <Button type="text" icon={isRTL ? <ArrowRightOutlined /> : <ArrowLeftOutlined />} onClick={() => setSidebarMode('list')} />
                        )}
                        <Title level={4} style={{ margin: 0, color: themeColors.text, fontSize: isMobile ? 16 : undefined }}>{agent?.name || t('agent_testing') || 'Agent testing'}</Title>
                    </div>
                    {(!isMobile || (isMobile && sidebarMode !== 'chat')) && (
                        <Button icon={<CloseOutlined />} type="text" onClick={handleClose} />
                    )}
                </Header>
                <Layout style={{ height: '100%' }}>
                    {!isMobile && (
                        <Sider width={280} style={{
                            background: isDarkMode ? '#141414' : '#fff',
                            borderRight: `1px solid ${themeColors.border}`,
                            padding: 16,
                            overflow: 'hidden'
                        }}>
                            {sidebarMode === 'new' ? renderSidebarNewContact() : renderSidebarList()}
                        </Sider>
                    )}
                    <Content style={{ height: '100%', overflow: 'hidden' }}>
                        {isMobile ? (
                            sidebarMode === 'chat' ? (
                                loading ? renderLoadingState() : renderChatInterface()
                            ) : sidebarMode === 'new' ? (
                                renderSidebarNewContact()
                            ) : (
                                renderSidebarList()
                            )
                        ) : (
                            loading ? renderLoadingState() : renderChatInterface()
                        )}
                    </Content>
                </Layout>
            </Layout>

            {/* Delete Contact Modal */}
            <ResponsiveModal
                title={t('delete_contact')}
                open={deleteModalVisible}
                onOk={confirmDeleteContact}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setContactToDelete(null);
                }}
                okText={t('delete')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                desktopWidth={420}
            >
                <div>{t('are_you_sure_you_want_to_delete_this_contact')}</div>
            </ResponsiveModal>
        </Modal>
    );
};

export default AgentTestModal; 